import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { X } from "lucide-react";
import { RootState, store } from "../../redux/store";
import { ModalProps } from "../../interfaces/connected-components/modal";
import { ModalType } from "../../shared/utils/enums";
import { hideModal } from "./reducer";
import Button from "../../components/button";
import { ButtonVariant } from "../../shared/utils/enums";
import ModalHeader from "../../components/modalHeader";

const Modal: React.FC<ModalProps> = ({
  isVisible,
  type,
  title,
  message,
  component,
  onConfirm,
  onCancel,
  onClose,
  width,
}): JSX.Element | null => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
      // Small delay to ensure the initial state is applied before animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsMounted(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!isMounted) return null;

  const handleClose = () => {
    store.dispatch(hideModal());
    onClose?.();
  };

  const renderContent = () => {
    switch (type) {
      case ModalType.QUESTION:
        return (
          <div className="flex flex-col gap-6 p-4">
            <div>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  onCancel?.();
                  handleClose();
                }}
                variant={ButtonVariant.SECONDARY}
                className="px-4 py-2 text-sm font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onConfirm?.();
                  handleClose();
                }}
                variant={ButtonVariant.DELETE}
                className="px-4 py-2 text-sm font-medium"
              >
                Delete
              </Button>
            </div>
          </div>
        );
      case ModalType.COMPONENT:
        return <div>{component}</div>;
      case ModalType.INFO:
        return (
          <div className="flex flex-col gap-4">
            <p className="text-gray-700 dark:text-gray-300">{message}</p>
            <div className="flex justify-end">
              <Button
                onClick={handleClose}
                variant={ButtonVariant.PRIMARY}
                className="px-4 py-2 text-sm font-medium"
              >
                Close
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Determine modal width based on width prop or fallback to defaults
  const getModalWidth = () => {
    if (width) {
      return `md:w-[${width}]`;
    }

    // Fallback to existing logic for backward compatibility
    if (type === ModalType.QUESTION) {
      return "md:w-[400px]";
    }

    return "md:w-[80%]";
  };

  return (
    <div
      id="modal-container"
      className={`fixed top-0 left-0 w-full h-full z-[9999] bg-black/40 backdrop-blur-[1px] flex justify-center items-center p-4 transition-all duration-200 ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
      onClick={() => {
        onCancel?.();
        store.dispatch(hideModal());
      }}
    >
      <div
        className={`bg-white dark:bg-[#181818] rounded-lg shadow-xl w-[96%] ${getModalWidth()} mx-4 flex flex-col max-h-[90vh] transition-all duration-200 ${
          isAnimating
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title={title} onClose={handleClose} />
        <div className="px-3 overflow-y-auto">{renderContent()}</div>
      </div>
    </div>
  );
};

const ModalContainer: React.FC = () => {
  const modalState = useSelector((state: RootState) => state.modal);
  const dispatch = useDispatch();

  if (!modalState.isVisible) return null;

  return <Modal {...modalState} closeModal={() => dispatch(hideModal())} />;
};

export default ModalContainer;
